import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, Subject, TimeoutError, firstValueFrom, timer } from 'rxjs';
import { retry, timeout } from 'rxjs/operators';
import { AuthStore } from '../../store/auth.store';
import { environment } from '../../../environments/environment';
import { LoginResponseSchema, UserMeResponseSchema } from '../../shared/schemas/usuario.schema';
import { ValidationService } from '../../shared/services/validation.service';
import { NetworkService } from '../../shared/services/network.service';

/** Timeout por intento en login LDAP (ms). */
const LOGIN_TIMEOUT_MS = 10_000;
/** Reintentos adicionales ante fallos transitorios. */
const LOGIN_RETRY_COUNT = 1;
/** Espera entre reintentos (ms). */
const LOGIN_RETRY_DELAY_MS = 1_500;
/** Mensaje tras agotar timeout/5xx/red. */
const MENSAJE_SERVIDOR_NO_DISPONIBLE =
  'No pudimos conectar con el servidor. Intente nuevamente en unos minutos.';

/**
 * Permisos de visitas asociados a un perfil (respuesta de /me).
 * "crear" se unificó con "agendar".
 */
export interface PermisosVisitas {
  agendar: boolean;
  cancelar: boolean;
  eliminar: boolean;
  registrar: boolean;
  reagendar: boolean;
  ver_detalles: boolean;
}

/**
 * Nodo permisos de un perfil
 */
export interface PermisosPerfil {
  visitas: PermisosVisitas;
}

/**
 * Interfaz para representar un perfil de usuario
 */
export interface Perfil {
  id_perfil: number;
  descripcion: string;
  permisos?: PermisosPerfil;
}

/**
 * Interfaz para representar un usuario según la nueva API
 */
export interface Usuario {
  sub: string;
  username: string;
  nombre: string;
  email: string;
  rut: string;
  tipo_acceso: number;
  perfiles: Perfil[];
  exp?: number;
  acepta_terminos?: boolean;
}

/** Resultado de autenticación con Clave Única; `errorDetail` refleja el cuerpo del error (p. ej. FastAPI `detail`). */
export interface ClaveUnicaAuthResult {
  success: boolean;
  errorDetail?: string;
  /** true cuando las credenciales son válidas pero el usuario no tiene perfiles activos (403). */
  accesoDenegado?: boolean;
}

/**
 * Servicio de autenticación
 * Maneja el login, registro y estado de autenticación
 * Usa AuthStore (similar a Zustand) en lugar de localStorage directamente
 */
@Injectable({
  providedIn: 'root'
})
export class AutenticacionService {
  private apiUrl = environment.apiUrl;

  private readonly router = inject(Router);
  private readonly authStore = inject(AuthStore);
  private readonly http = inject(HttpClient);
  private readonly validationService = inject(ValidationService);
  private readonly networkService = inject(NetworkService);

  public usuarioActual$: Observable<Usuario | null> = this.authStore.usuario$;
  public estaAutenticado$: Observable<boolean> = this.authStore.estaAutenticado$;

  /** Progreso del login LDAP para actualizar el overlay (iniciando / reintentando). */
  private readonly loginProgresoSubject = new Subject<'iniciando' | 'reintentando'>();
  readonly loginProgreso$ = this.loginProgresoSubject.asObservable();

  /**
   * Obtiene el usuario actual
   */
  obtenerUsuarioActual(): Usuario | null {
    return this.authStore.obtenerUsuarioActual();
  }

  /**
   * Verifica si el usuario está autenticado
   */
  obtenerEstadoAutenticacion(): boolean {
    return this.authStore.obtenerEstadoAutenticacion();
  }

  /**
   * Verifica si hay conectividad a internet
   */
  tieneConectividad(): boolean {
    return this.networkService.isOnline();
  }

  /** Extrae `detail` (o equivalente) del cuerpo de un HttpErrorResponse / error de red. */
  private extractHttpErrorDetail(error: unknown): string | undefined {
    if (!error || typeof error !== 'object') {
      return undefined;
    }
    const err = error as { error?: unknown };
    const body = err.error;
    if (typeof body === 'string' && body.trim()) {
      return body;
    }
    if (!body || typeof body !== 'object') {
      return undefined;
    }
    const detail = (body as { detail?: unknown }).detail;
    if (typeof detail === 'string') {
      return detail;
    }
    if (Array.isArray(detail) && detail.length > 0) {
      const first = detail[0];
      if (typeof first === 'string') {
        return first;
      }
      if (first && typeof first === 'object' && 'msg' in first) {
        const msg = (first as { msg?: unknown }).msg;
        if (typeof msg === 'string') {
          return msg;
        }
      }
    }
    return undefined;
  }

  private esTimeout(error: unknown): boolean {
    if (error instanceof TimeoutError) {
      return true;
    }
    return !!error && typeof error === 'object' && 'name' in error
      && (error as { name: string }).name === 'TimeoutError';
  }

  /** Timeout, red (status 0) o 5xx: candidatos a reintento. */
  private esErrorTransitorio(error: unknown): boolean {
    if (this.esTimeout(error)) {
      return true;
    }
    if (error instanceof HttpErrorResponse) {
      return error.status === 0 || error.status >= 500;
    }
    const status = (error as { status?: number })?.status;
    return status === 0 || (typeof status === 'number' && status >= 500);
  }

  /**
   * Inicia sesión con username y contraseña
   * @param username Username del usuario (email)
   * @param password Contraseña del usuario
   * @returns AuthResult que indica éxito o error con detalle
   */
  async iniciarSesion(username: string, password: string, tipoLogin?: string): Promise<ClaveUnicaAuthResult> {
    if (!this.tieneConectividad()) {
      console.log('Sin conectividad, no se puede autenticar');
      return { success: false, errorDetail: 'Sin conexión a internet' };
    }

    this.loginProgresoSubject.next('iniciando');

    try {
      // Paso 1: Obtener el token (timeout + reintentos ante fallos transitorios)
      const loginResponseRaw: any = await firstValueFrom(
        this.http.post(`${this.apiUrl}/autenticar/login`, { username, password, tipo_login: tipoLogin }).pipe(
          timeout({ first: LOGIN_TIMEOUT_MS }),
          retry({
            count: LOGIN_RETRY_COUNT,
            delay: (error) => {
              if (!this.esErrorTransitorio(error)) {
                throw error;
              }
              this.loginProgresoSubject.next('reintentando');
              return timer(LOGIN_RETRY_DELAY_MS);
            }
          })
        )
      );

      // Validar la respuesta del login con Zod
      const loginValidation = this.validationService.validate(
        LoginResponseSchema,
        loginResponseRaw
      );

      if (!loginValidation.success) {
        console.error('Error al validar respuesta de login:', loginValidation.errorMessages);
        return { success: false, errorDetail: 'Error de validación del servidor' };
      }

      const accessToken = loginValidation.data!.access_token;
      const refreshToken = loginValidation.data!.refresh_token;

      // Paso 2: Obtener la información del usuario usando el token
      const userInfoResponseRaw: any = await firstValueFrom(
        this.http.get(`${this.apiUrl}/autenticar/me`, {
          headers: {
            Authorization: `Bearer ${accessToken}`
          }
        }).pipe(
          timeout({ first: LOGIN_TIMEOUT_MS })
        )
      );

      // Validar la respuesta del usuario con Zod
      const userValidation = this.validationService.validate(
        UserMeResponseSchema,
        userInfoResponseRaw
      );

      if (!userValidation.success) {
        console.error('Error al validar información del usuario:', userValidation.errorMessages);
        return { success: false, errorDetail: 'Error al obtener perfil de usuario' };
      }

      const userInfoResponse = userValidation.data!;

      const usuarioAutenticado: Usuario = {
        sub: userInfoResponse.sub,
        username: userInfoResponse.username,
        nombre: userInfoResponse.nombre,
        email: userInfoResponse.email,
        rut: userInfoResponse.rut,
        tipo_acceso: userInfoResponse.tipo_acceso,
        perfiles: (userInfoResponse.perfiles || []).map((p: Perfil) => ({
          id_perfil: p.id_perfil,
          descripcion: p.descripcion,
          permisos: p.permisos
        })),
        exp: userInfoResponse.exp,
        acepta_terminos: userInfoResponse.acepta_terminos
      };
      
      // Usar el store para actualizar el estado con el usuario y el token
      this.authStore.iniciarSesionConProveedor(usuarioAutenticado, accessToken, (tipoLogin ?? 'ldap') === 'claveunica' ? 'claveunica' : 'ldap', refreshToken);
      return { success: true };
    } catch (error: any) {
      console.error('Error al iniciar sesión:', error);
      const errorDetail = this.extractHttpErrorDetail(error);
      const status = error?.status as number | undefined;

      if (status === 403) {
        return { success: false, errorDetail, accesoDenegado: true };
      }

      if (status === 401) {
        return { success: false, errorDetail };
      }

      if (status === 429) {
        return { success: false, errorDetail };
      }

      if (this.esTimeout(error) || status === 0 || (typeof status === 'number' && status >= 500)) {
        return { success: false, errorDetail: MENSAJE_SERVIDOR_NO_DISPONIBLE };
      }
      
      return { success: false, errorDetail };
    }
  }

  /**
 * Inicia sesión con ClaveÚnica usando el JWT recibido en el callback
 * @param jwt Token JWT entregado por ClaveÚnica
 * @returns Resultado con `errorDetail` del backend cuando falla (p. ej. 401 `Usuario no registrado`)
 */
async iniciarSesionClaveUnica(jwt: string): Promise<ClaveUnicaAuthResult> {
  if (!this.tieneConectividad()) {
    console.log('Sin conectividad, no se puede autenticar');
    return { success: false, errorDetail: 'Sin conexión a internet' };
  }

  try {
    // Paso 1: Validar el JWT en tu backend
    const loginResponseRaw: any = await firstValueFrom(
      this.http.post(`${this.apiUrl}/autenticar/claveunica`, { jwt })
    );

    // Validar la respuesta del backend con Zod (igual que en login normal)
    const loginValidation = this.validationService.validate(
      LoginResponseSchema,
      loginResponseRaw
    );

    if (!loginValidation.success) {
      console.error('Error al validar respuesta de ClaveÚnica:', loginValidation.errorMessages);
      return { success: false };
    }

    const accessToken = loginValidation.data!.access_token;
    const refreshToken = loginValidation.data!.refresh_token;

    // Paso 2: Obtener la información del usuario usando el token interno
    const userInfoResponseRaw: any = await firstValueFrom(
      this.http.get(`${this.apiUrl}/autenticar/me`, {
        headers: {
          Authorization: `Bearer ${accessToken}`
        }
      })
    );

    const userValidation = this.validationService.validate(
      UserMeResponseSchema,
      userInfoResponseRaw
    );

    if (!userValidation.success) {
      console.error('Error al validar información del usuario:', userValidation.errorMessages);
      return { success: false };
    }

    const userInfoResponse = userValidation.data!;

    const usuarioAutenticado: Usuario = {
      sub: userInfoResponse.sub,
      username: userInfoResponse.username,
      nombre: userInfoResponse.nombre,
      email: userInfoResponse.email,
      rut: userInfoResponse.rut,
      tipo_acceso: userInfoResponse.tipo_acceso,
      perfiles: (userInfoResponse.perfiles || []).map((p: Perfil) => ({
        id_perfil: p.id_perfil,
        descripcion: p.descripcion,
        permisos: p.permisos
      })),
      exp: userInfoResponse.exp,
      acepta_terminos: userInfoResponse.acepta_terminos
    };

    // Actualizar el store con el usuario y el token
    this.authStore.iniciarSesionConProveedor(usuarioAutenticado, accessToken, 'claveunica', refreshToken);
    return { success: true };
  } catch (error: unknown) {
    console.error('Error al iniciar sesión con ClaveÚnica:', error);
    const errorDetail = this.extractHttpErrorDetail(error);

    const status = (error as { status?: number })?.status;
    if (status === 403) {
      return { success: false, errorDetail, accesoDenegado: true };
    }

    if (status === 401) {
      return { success: false, errorDetail };
    }

    if (status === 0 || (status !== undefined && status >= 500)) {
      console.log('Servidor no disponible');
    }

    return { success: false, errorDetail };
  }
}

  /**
   * Registra un nuevo usuario
   * NOTA: El registro no está disponible a través de la API centralizada.
   * Los usuarios deben ser creados por un administrador.
   * @param email Email del nuevo usuario
   * @param contraseña Contraseña del nuevo usuario
   * @param nombre Nombre del nuevo usuario
   * @returns Promise que resuelve si el registro es exitoso (siempre false)
   */
  async registrar(email: string, contraseña: string, nombre: string): Promise<boolean> {
    console.warn('El registro de usuarios no está disponible a través de la API centralizada. Los usuarios deben ser creados por un administrador.');
    return false;
  }

  /**
   * Acepta los términos y condiciones para el usuario actual
   */
  async aceptarTerminos(): Promise<boolean> {
    try {
      const response: any = await firstValueFrom(
        this.http.post(`${this.apiUrl}/autenticar/terminos/aceptar`, {})
      );

      // La API devuelve un nuevo token con acepta_terminos en true
      if (response && response.access_token) {
        // Actualizamos el usuario en el store para reflejar que aceptó los términos
        const usuario = this.authStore.obtenerUsuarioActual();
        if (usuario) {
          usuario.acepta_terminos = true;
          this.authStore.iniciarSesionConProveedor(
            usuario,
            response.access_token,
            this.authStore.obtenerProveedorAuth() || 'ldap',
            response.refresh_token
          );
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error al aceptar términos:', error);
      return false;
    }
  }

  /** Promesa de refresh en curso, si existe (silenciosa / colapsa llamadas paralelas). */
  private refreshEnCurso: Promise<boolean> | null = null;

  /**
   * Renueva la sesión usando el refresh token.
   * Devuelve true si el refresh fue exitoso y actualizó los tokens en el store.
   *
   * Implementa un lock compartido: si varias peticiones (guard, interceptor, timer)
   * intentan refrescar a la vez, todas reutilizan LA MISMA promesa. Esto es
   * imprescindible porque el backend ROTA el refresh token: un refresh en paralelo
   * con el mismo token fallaría con 401 (Sesión revocada) y cerraría la sesión.
   */
  async refrescarToken(): Promise<boolean> {
    if (this.refreshEnCurso) {
      return this.refreshEnCurso;
    }
    this.refreshEnCurso = this.ejecutarRefresh().finally(() => {
      this.refreshEnCurso = null;
    });
    return this.refreshEnCurso;
  }

  /** Ejecuta una única renovación de sesión. Nunca debe correr en paralelo. */
  private async ejecutarRefresh(): Promise<boolean> {
    const refreshToken = this.authStore.obtenerRefreshToken();
    if (!refreshToken) {
      return false;
    }

    try {
      const response: any = await firstValueFrom(
        this.http.post(`${this.apiUrl}/autenticar/refresh`, { refresh_token: refreshToken })
      );

      const validation = this.validationService.validate(LoginResponseSchema, response);
      if (!validation.success || !validation.data?.access_token || !validation.data.refresh_token) {
        console.error('Respuesta inválida al refrescar token:', validation.errorMessages);
        return false;
      }

      this.authStore.actualizarTokens(validation.data.access_token, validation.data.refresh_token);
      return true;
    } catch (error) {
      console.warn('Error al refrescar token:', error);
      return false;
    }
  }

  /**
   * Cierra la sesión del usuario actual
   */
  cerrarSesion(): void {
    const proveedorAuth = this.authStore.obtenerProveedorAuth();
    const eraClaveUnica = proveedorAuth === 'claveunica';

    // Notificar al backend para revocar el refresh token (si hay token disponible).
    if (this.authStore.obtenerToken()) {
      this.http.post(`${this.apiUrl}/autenticar/logout`, {}).subscribe({
        error: () => { /* El logout local se hace igual aunque el backend falle */ }
      });
    }

    // Usar el store para cerrar sesión (siempre)
    this.authStore.cerrarSesion();
    const loginRoute = '/login';

    // Si el login fue vía ClaveÚnica, también cerrar sesión en ClaveÚnica
    if (eraClaveUnica && this.networkService.isOnline()) {
      const redirectUrl = `${window.location.origin}${loginRoute}`;
      const url = `${environment.claveUnica.logoutBaseUrl}?redirect=${encodeURIComponent(redirectUrl)}`;
      window.location.href = url;
      return;
    }

    // Navegación SPA, con fallback a navegación dura si algo bloquea el router.
    void this.router.navigate([loginRoute], { replaceUrl: true });
    setTimeout(() => {
      const path = window.location.pathname.replace(/\/$/, '') || '/';
      if (path !== loginRoute) {
        window.location.replace(`${window.location.origin}${loginRoute}`);
      }
    }, 50);
  }
}

